import * as moment from 'moment';
import { SecurityHelper } from './securityHelper';

export class DataFormatter {

  static ToEuroNumberFormat(val: number, minDecimalPlace?: number, maxDecimalPlace?: number): string {

    // round up number based on maxDecimalPlace
    if (maxDecimalPlace) {
      let decimalPlace = Math.pow(10, maxDecimalPlace);
      val = Math.round(val * decimalPlace) / decimalPlace;
    } else {
      // if maxDecimalPlace not set, set to actual decimal place to avoid rounding of numbers
      if (val) {
        if (Math.floor(val) !== val) {
          maxDecimalPlace = (val.toString().split(".").length > 1) ? val.toString().split(".")[1].length : 0;
          if (maxDecimalPlace < minDecimalPlace!) {
            maxDecimalPlace = minDecimalPlace;
          }
        }
      }
    }

    // Belgian number format
    let locale = 'nl-BE';

    let userPreferenceCulture = SecurityHelper.GetUserPreference(SecurityHelper.GetLoggedInUserDto(), SecurityHelper.PreferenceIDs.CULTURE);
    if (userPreferenceCulture && userPreferenceCulture == 'American') {
      // American number format
      locale = 'en-US';
    }

    return new Intl.NumberFormat(locale,
      {
        minimumFractionDigits: minDecimalPlace,
        maximumFractionDigits: maxDecimalPlace
      }).format(val);
  }

  static ToEuroDateFormat(val: any): string {

    // en-GB : dd/mm/yyyy
    let locale = 'en-GB';

    let userPreferenceCulture = SecurityHelper.GetUserPreference(SecurityHelper.GetLoggedInUserDto(), SecurityHelper.PreferenceIDs.CULTURE);
    if (userPreferenceCulture && userPreferenceCulture == 'American') {
      // American number format
      locale = 'en-US';
    }

    let returnVal = '';
    if (val)
      returnVal = new Intl.DateTimeFormat(locale).format(new Date(val));

    return returnVal;

  }

  static ToDateByCulture(val: any): Date | null {
    if (!val) return null;

    const userPreferenceCulture = SecurityHelper.GetUserPreference(
      SecurityHelper.GetLoggedInUserDto(),
      SecurityHelper.PreferenceIDs.CULTURE
    );

    // If already a Date object
    if (val instanceof Date) {
      return val;
    }

    const input = String(val).trim();
    const parts = input.split('/');

    if (parts.length !== 3) {
      const parsed = new Date(input);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    const [part1, part2, year] = parts.map(Number);

    let day: number;
    let month: number;

    if (userPreferenceCulture === 'American') {
      // MM/DD/YYYY
      month = part1;
      day = part2;
    } else {
      // DD/MM/YYYY
      day = part1;
      month = part2;
    }

    const result = new Date(year, month - 1, day);

    return isNaN(result.getTime()) ? null : result;
  }

  /** Formats "DD/MM/YYYY" euro date format or "YYYY-MM-DD" ISO date format into local date format, no timezone shift */
  static ToDate(euroDateFormat: string): Date {
    let formattedVal: any = null;

    formattedVal = this.ToLocalDate(euroDateFormat);

    return formattedVal;
  }

  /** Formats "DD/MM/YYYY" euro date format or "YYYY-MM-DD" ISO date format into local date format, no timezone shift */
  static ToLocalDate(input: string | null | undefined): Date | null {
    if (!input) return null;

    // Handle YYYY-MM-DD
    if (input.includes('-')) {
      const parts = input.split('-');
      if (parts.length !== 3) return null;

      const [yyyy, mm, dd] = parts.map(Number);

      if (!yyyy || !mm || !dd) return null;
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;

      return new Date(yyyy, mm - 1, dd);
    }

    // Handle DD/MM/YYYY
    if (input.includes('/')) {
      const parts = input.split('/');
      if (parts.length !== 3) return null;

      const [dd, mm, yyyy] = parts.map(Number);

      if (!yyyy || !mm || !dd) return null;
      if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null;

      return new Date(yyyy, mm - 1, dd);
    }

    return null;
  }

  /**
   * Accepts a euro string date "DD/MM/YYYY" or local date and converts to a safe ISO date-only string "YYYY-MM-DD".
   * Use this for when sending date to APIs to prevent timezone-related date shifting caused by Date → UTC serialization.
   */
  static ToDateOnlyString(input: string | Date | null | undefined): string {

    let d: Date | null;

    if (input instanceof Date) {
      d = input;
    } else {
      d = this.ToLocalDate(input);
    }

    if (!d || isNaN(d.getTime())) return '';

    const month = d.getMonth() + 1;
    const day = d.getDate();

    const mm = month < 10 ? '0' + month : month.toString();
    const dd = day < 10 ? '0' + day : day.toString();

    return `${d.getFullYear()}-${mm}-${dd}`;
  }

  static ToDateMoment(euroDateFormat: string): Date {
    let formattedVal: any = null;

    if (euroDateFormat) {
      let dateSplit = euroDateFormat.split('/');

      formattedVal = moment.utc([dateSplit[2], parseInt(dateSplit[1]) - 1, dateSplit[0], 5]);//.toDate();
    }

    return formattedVal;
  }

  // format "###,###.##" to proper number format
  static ToNumber(euroNumberFormat: string): number {
    let formattedVal: any = null;

    // remove thousand separator, parseFloat can not read thousand separator properly
    if (euroNumberFormat) {
      let stringValue = euroNumberFormat.toString();

      let userPreferenceCulture = SecurityHelper.GetUserPreference(SecurityHelper.GetLoggedInUserDto(), SecurityHelper.PreferenceIDs.CULTURE);
      if (userPreferenceCulture && userPreferenceCulture == 'American') {
        // American number format in string to number
        if (stringValue.indexOf(',') > -1) {
          formattedVal = stringValue.replace(/,/g, "");
          formattedVal = parseFloat(formattedVal);
        }
        else {
          formattedVal = parseFloat(stringValue);
        }
      }
      else {
        // Belgian number format in string to number
        formattedVal = stringValue.replace(/[,.]/g, function (x) { return x == "," ? "." : ""; });
        formattedVal = parseFloat(formattedVal);
      }
    }

    return formattedVal;
  }

  //// format "###,###.##" to proper number format for Stocks
  //static ToNumber_Quantity(euroNumberFormat: string): number {
  //  let formattedVal: any = null;

  //  // remove thousand separator, parseFloat can not read thousand separator properly
  //  if (euroNumberFormat) {
  //    let stringValue = euroNumberFormat.toString();

  //    let userPreferenceCulture = SecurityHelper.GetUserPreference(SecurityHelper.GetLoggedInUserDto(), SecurityHelper.PreferenceIDs.CULTURE);
  //    if (userPreferenceCulture && userPreferenceCulture == 'American') {
  //      // American number format in string to number
  //      if (stringValue.indexOf(',') > -1) {
  //        formattedVal = stringValue.replace(/[,.]/g, function (x) { return x == "," ? "" : "."; });
  //        formattedVal = parseFloat(formattedVal);
  //      }
  //      else {
  //        formattedVal = parseFloat(stringValue);
  //      }
  //    }
  //    else {
  //      // Belgian number format in string to number
  //      formattedVal = stringValue.replace(/[,.]/g, function (x) { return x == "." ? "" : "."; });
  //      formattedVal = parseFloat(formattedVal);
  //    }
  //  }

  //  return formattedVal;
  //}

  // format "###,###.##" to proper number format for Stocks
  static ToNumber_Quantity(euroNumberFormat: string): number {
    let formattedVal: number = 0;

    if (euroNumberFormat) {
      let stringValue = euroNumberFormat.toString();

      // for parsing user input, may be American or Belgian format
      // disregards user preference since some user will use wrong separator
      // assumes format based on last separator
      let lastIndexPeriod = stringValue.lastIndexOf('.');
      let lastIndexComma = stringValue.lastIndexOf(',');
      let lastIndex = -1;

      if (lastIndexPeriod > - 1 || lastIndexComma > -1) {
        lastIndex = lastIndexPeriod > lastIndexComma ? lastIndexPeriod : lastIndexComma;
        if (stringValue.substring(lastIndex + 1).length < 3) { // if digits after last separator is 2 or less then assume decimal, ignore other separator
          formattedVal = parseFloat(stringValue.substring(0, lastIndex).replace(/[,.]/g, "") + '.' + stringValue.substring(lastIndex + 1))
        }
        else { // if digits after last separator is 3 or more, ignore all separator
          formattedVal = parseFloat(stringValue.replace(/[,.]/g, ""))
        }
      }
      else { // if no separators, parse normally
        formattedVal = parseFloat(stringValue);
      }
    }

    return formattedVal;
  }

  static ToBoolean(bFormat: any): boolean {
    let formattedVal: any = null;

    // remove thousand separator, parseFloat can not read thousand separator properly
    if (typeof bFormat === 'boolean') {
      formattedVal = bFormat;
    }
    else {
      if (bFormat.toUpperCase() == 'TRUE')
        formattedVal = true;
      else
        formattedVal = false;
    }
    return formattedVal;
  }

  static RoundNumber(paramValue: number, paramDecimalPlace: number): number {

    // fix precision
    let outputValue = paramValue;

    let str = ' ' + paramValue.toString();
    let decimalPosition = 0;

    if (str.indexOf('.') >= 0) {
      decimalPosition = str.split('.').length > 0 ? str.split('.')[1].length : 0;
    }

    if (decimalPosition > 0) {

      let lastDecimalValue = parseInt(str[str.length - 1]);
      let decimalPlace = (Math.pow(10, decimalPosition));

      // issue: Math.round 4.725 = 4.72
      // workaround:
      // -- 4.725 + 0.001 = 4.726
      // -- Math.round(4.726) = 4.73
      if (decimalPlace == 1000 && lastDecimalValue == 5) {
        let addDecimalValue = 1 / decimalPlace;
        outputValue = outputValue + addDecimalValue;
        outputValue = parseFloat(outputValue.toFixed(3));
      }
    }

    // round number
    let roundToDecimalPlace = (Math.pow(10, paramDecimalPlace));
    let roundedNumber = Math.round((outputValue + Number.EPSILON) * roundToDecimalPlace) / roundToDecimalPlace;

    return roundedNumber;
  }

  static makeNumArray(arrStr: string): any[] {
    let arr: any[];
    let numArr: number[] = [];
    if (arrStr != null) {
      arr = arrStr.split(',');
      arr.forEach(x => {
        numArr.push(parseInt(x));
      })
    }
    return numArr;
  }
  static makeCommaSeparatedString(arr: any[]): string {
    let str = '';
    if (arr != null && arr.length > 0) {
      arr.forEach(x => {
        if (str == '' || str == null) {
          str = x;
        }
        else {
          str += ',' + x;
        }
      })
    }
    return str;
  }

}
